import { useEffect } from "react";
import { useLocation, type ParsedLocation } from "@tanstack/react-router";

// Where you came from, so "terug" top left on a card goes back to the screen you were
// working in — the order you opened a location from — instead of starting over at the
// card's own list (Camiel, 23-09-2026: "ik wil dan linksboven terug kunnen drukken naar de
// order waarmee ik bezig was ... je moet elke keer nu opnieuw beginnen").
//
// It follows the browser history rather than keeping a stack of its own: every screen is
// written down under its place in the history (the router's __TSR_index), and going back
// is history.go(-n). So the trail shrinks and grows with the browser's own back and forward
// buttons too, and the screen you land on is the one you left, filters and tab included.
// A card puts its name on its entry (useScreenLabel), so the link can say where it goes.
//
// sessionStorage, like lib/listState: the history of a tab survives F5, so its trail must
// too; another tab or tomorrow starts clean.

const KEY = "wlx:screenTrail";
// How far back the link looks. Deeper than anyone clicks through; older entries are dropped.
const DEPTH = 50;

type Entry = { pathname: string; href: string; label?: string };
type Location = Pick<ParsedLocation, "pathname" | "href" | "state">;

let trail: Record<number, Entry> | null = null;

function load(): Record<number, Entry> {
  if (trail) return trail;
  trail = {};
  try {
    const raw = sessionStorage.getItem(KEY);
    if (raw) trail = JSON.parse(raw) as Record<number, Entry>;
  } catch {
    /* no storage (private mode) or unreadable — this tab starts without a trail */
  }
  return trail;
}

function save(): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(trail));
  } catch {
    /* storage full or unavailable — the in-memory copy still covers this tab */
  }
}

/** The place of a location in the browser history, or null outside the router's history. */
export function historyIndex(location: Pick<ParsedLocation, "state">): number | null {
  const index = (location.state as { __TSR_index?: unknown } | undefined)?.__TSR_index;
  return typeof index === "number" ? index : null;
}

/** Called on every navigation the router completes (main.tsx). */
export function recordScreen(location: Location): void {
  const index = historyIndex(location);
  if (index === null) return;
  const entries = load();
  const known = entries[index];
  entries[index] = {
    pathname: location.pathname,
    href: location.href,
    // A card may have named itself before the router finished; a new screen starts nameless.
    label: known?.pathname === location.pathname ? known.label : undefined,
  };
  for (const key of Object.keys(entries)) {
    if (Number(key) < index - DEPTH) delete entries[Number(key)];
  }
  save();
}

function labelScreen(location: Location, label: string): void {
  const index = historyIndex(location);
  if (index === null) return;
  const entries = load();
  const known = entries[index];
  entries[index] = known?.pathname === location.pathname ? { ...known, label } : { pathname: location.pathname, href: location.href, label };
  save();
}

/** A card names its entry — its order number, the name of the place — for the link back to it. */
export function useScreenLabel(label: string | null | undefined): void {
  const location = useLocation();
  const index = historyIndex(location);
  useEffect(() => {
    if (label) labelScreen(location, label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label, index, location.pathname]);
}

export interface PreviousScreen extends Entry {
  /** How many steps back in the history it lies. */
  steps: number;
}

/**
 * The screen before this one, skipping entries of this same screen (a tab switch on a card
 * is a step in the history too). Null when there is none this tab knows of.
 */
export function previousScreen(location: Location): PreviousScreen | null {
  const index = historyIndex(location);
  if (index === null) return null;
  const entries = load();
  for (let at = index - 1; at >= 0 && index - at <= DEPTH; at--) {
    const entry = entries[at];
    if (!entry) return null;
    if (entry.pathname === location.pathname) continue;
    return { ...entry, steps: index - at };
  }
  return null;
}
